<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import PageHeader from '@/components/PageHeader.vue'
import AIDisclaimer from '@/components/AIDisclaimer.vue'
import AssessmentWizardPanel from '@/assessments/AssessmentWizardPanel.vue'
import AssessmentWizardForm from '@/assessments/AssessmentWizardForm.vue'
import AssessmentLoadingScreen from '@/assessments/AssessmentLoadingScreen.vue'
import { sanitizeQuery, truncateQueryForEvent } from '@/assessments/assessment_wizard_query'
import { useAgenticSearchStore } from '@/search/agentic_search.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { betaOptIn } from '@/util/beta_opt_in'

const FROM_WIZARD_REF = 'wizard'

const route = useRoute()
const router = useRouter()
const analytics = getAnalyticsTracker()
const agenticSearch = useAgenticSearchStore()
const { loading: isSearching, error: searchError, hasAssessmentResults, latestSearch } = storeToRefs(agenticSearch)
const { aiFeatureVisibility } = storeToRefs(useVisibilityStore())
const userStore = useUserStore()
const { fetchingActiveProfile } = storeToRefs(useContentProfileStore())
// aiFeatureVisibility reads true until the content profile loads, so the page view waits for it.
const isProfileLoaded = computed(() => !userStore.fetching && !fetchingActiveProfile.value)

const query = ref('')
const files = ref<File[]>([])
const gradeLevels = ref<string[]>([])
const isGradeAccordionOpen = ref(false)
const wizardForm = ref<InstanceType<typeof AssessmentWizardForm> | null>(null)
let isHandingOffToResults = false

const showsSearchProgress = computed(() => isSearching.value || Boolean(searchError.value))

// Edit search on Browse Assessments sends the user back here with ref=wizard to refine the
// search they ran; any other arrival starts a new one.
function restoreLatestSearch() {
  query.value = latestSearch.value.prompt ?? ''
  gradeLevels.value = (latestSearch.value.searchGrade ?? '').split(',').filter(Boolean)
  isGradeAccordionOpen.value = gradeLevels.value.length > 0
}

function showResults() {
  isHandingOffToResults = true
  router.push({ name: 'BrowseAssessments', query: { ...betaOptIn(route.query), ref: FROM_WIZARD_REF } })
}

async function findAssessments() {
  const selectedGrades = [...gradeLevels.value]
  try {
    const searchId = await agenticSearch.searchAssessments({
      query: sanitizeQuery(query.value),
      files: files.value,
      filters: { gradeLevels: selectedGrades },
      searchGrade: selectedGrades.join(',')
    })
    analytics.sendEvent(EVENT_NAMES.ASSESSMENT_WIZARD.SUBMITTED, {
      search_query: truncateQueryForEvent(query.value),
      selected_grades: selectedGrades,
      agentic_search_id: searchId
    })
    // The first result hands off as soon as it streams in; a search that finds none hands off
    // once it completes, so Browse Assessments can say so.
    agenticSearch.addSearchCompleteCallback(() => {
      if (!agenticSearch.hasAssessmentResults) {
        showResults()
      }
    })
  } catch (e) {
    console.error('Error searching assessments:', e)
  }
}

async function restartSearch() {
  agenticSearch.clearResults()
  await nextTick()
  wizardForm.value?.focusQuery()
}

function sendDisclaimerOpenedEvent() {
  analytics.sendEvent(EVENT_NAMES.ASSESSMENT_WIZARD.OPENED_AI_DISCLAIMER)
}

if (route.query.ref === FROM_WIZARD_REF) {
  restoreLatestSearch()
} else {
  agenticSearch.clearSearchAndResults()
}

watch(hasAssessmentResults, (hasResults) => {
  if (hasResults) {
    showResults()
  }
})

watch(
  aiFeatureVisibility,
  (isVisible) => {
    if (!isVisible) {
      router.replace({ name: 'BrowseAssessments', query: route.query })
    }
  },
  { immediate: true }
)

// A search left running would otherwise finish later and pull the user back to its results
// from whatever page they moved on to.
onBeforeUnmount(() => {
  if (isSearching.value && !isHandingOffToResults) {
    agenticSearch.clearResults()
  }
})

function sendPageViewIfAIVisible() {
  if (aiFeatureVisibility.value) {
    analytics.sendEvent(EVENT_NAMES.ASSESSMENT_WIZARD.VIEWED_PAGE, route.query.ref ? { ref: route.query.ref } : {})
  }
}

if (isProfileLoaded.value) {
  sendPageViewIfAIVisible()
} else {
  watch(isProfileLoaded, sendPageViewIfAIVisible, { once: true })
}
</script>

<template>
  <div class="container">
    <PageHeader
      title="Assessment Wizard"
      subtitle="Discover targeted baseline and progress monitoring assessments from thousands of resources in our collection."
    />

    <AssessmentWizardPanel :theme="showsSearchProgress ? 'plain' : 'gradient'">
      <AssessmentLoadingScreen v-if="showsSearchProgress" :has-error="Boolean(searchError)" @restart="restartSearch" />
      <AssessmentWizardForm
        v-else
        ref="wizardForm"
        v-model:query="query"
        v-model:files="files"
        v-model:grade-levels="gradeLevels"
        v-model:grade-accordion-open="isGradeAccordionOpen"
        @submit="findAssessments"
      />
    </AssessmentWizardPanel>

    <div class="mt-6 flex justify-center">
      <AIDisclaimer class="w-full max-w-[530px] text-black" size="md" @opened="sendDisclaimerOpenedEvent" />
    </div>
  </div>
</template>
